// Bankroll-tier presets + pure helpers (migration 043). Kept separate from
// the BankrollPicker component so the arithmetic is unit-testable without a
// DOM, and shared by both the Fam + Fantasy Fam create modals.

import type { BankrollTier } from "./types";

export interface BankrollPreset {
	tier: BankrollTier;
	label: string;
	blurb: string;
	// Dollar amount the tier pins to; null for 'custom' (caller supplies it).
	usd: number | null;
	// Whether an active paid subscription is required to pick this tier.
	paidOnly: boolean;
}

// Order matches the picker layout. The three fixed amounts mirror the
// fams_bankroll_matches_tier / fantasy_fams_bankroll_matches_tier CHECKs.
export const BANKROLL_PRESETS: readonly BankrollPreset[] = [
	{
		tier: "10k",
		label: "$10K",
		blurb: "Free starting bankroll.",
		usd: 10_000,
		paidOnly: false,
	},
	{
		tier: "100k",
		label: "$100K",
		blurb: "Serious-money sizing.",
		usd: 100_000,
		paidOnly: true,
	},
	{
		tier: "1m",
		label: "$1M",
		blurb: "Whale bankroll.",
		usd: 1_000_000,
		paidOnly: true,
	},
	{
		tier: "custom",
		label: "Custom",
		blurb: "Name your own number.",
		usd: null,
		paidOnly: true,
	},
];

// Result of turning a (tier, custom-input) pair into POST body fields.
export type BankrollSelection =
	| { ok: true; bankrollTier: BankrollTier; bankrollUsd?: number }
	| { ok: false; error: string };

/**
 * resolveBankroll — validate a picker selection into the fields a create
 * request carries. Presets pass straight through (bankrollUsd is left to the
 * server). 'custom' requires a positive numeric amount; anything else is an
 * error the modal surfaces inline.
 */
export function resolveBankroll(
	tier: BankrollTier,
	customUsdInput: string,
): BankrollSelection {
	if (tier !== "custom") {
		return { ok: true, bankrollTier: tier };
	}
	const amount = Number(customUsdInput.trim());
	if (!Number.isFinite(amount) || amount <= 0) {
		return { ok: false, error: "Enter a positive custom bankroll amount." };
	}
	return { ok: true, bankrollTier: tier, bankrollUsd: amount };
}

/**
 * formatUsd — whole-dollar currency string (no cents). Accepts the numeric
 * dollar value or the numeric(20,2) string the API sends. Returns "—" for a
 * non-finite input so callers can render it directly.
 */
export function formatUsd(value: number | string): string {
	const n = typeof value === "string" ? Number(value) : value;
	if (!Number.isFinite(n)) return "—";
	return n.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	});
}
